import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkle, X, PaperPlaneTilt, Microphone, Trash, CheckCircle, Camera, ArrowSquareOut } from "@phosphor-icons/react";

const SESSION_KEY = "askproman_session_id";

function newSessionId() {
  const id = "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

function getSessionId() {
  return localStorage.getItem(SESSION_KEY) || newSessionId();
}

/**
 * Floating "Ask Radar" AI assistant. Mounts on every admin page.
 * - Answers questions about jobs / workers / properties from a live server snapshot
 * - Creates jobs from natural language ("Add a job at 5028 N 12th for the water heater")
 * - Voice input via existing Whisper endpoint
 */
export default function AskProManBubble() {
  const location = useLocation();
  const isTeamChat = location.pathname.includes("/team-chat");
  const [open, setOpenState] = useState(() => sessionStorage.getItem("askproman_open") === "1");
  const setOpen = (v) => { setOpenState(v); try { sessionStorage.setItem("askproman_open", v ? "1" : "0"); } catch { /* ignore */ } };
  const navigate = useNavigate();
  const pendingAskRef = useRef(null);

  // Other components (Ctrl+K palette, search "no results") hand a question to Radar via this event
  useEffect(() => {
    const onAsk = (e) => { const msg = e.detail?.message; setOpen(true); if (msg) pendingAskRef.current = msg; };
    window.addEventListener("proman:ask", onAsk);
    return () => window.removeEventListener("proman:ask", onAsk);
  }, []);

  const pageSnapshot = () => {
    const ctxEl = document.querySelector("[data-proman-context]");
    const h1 = document.querySelector("main h1, h1");
    return {
      path: location.pathname + location.search,
      title: (h1?.textContent || document.title || "").trim().slice(0, 120),
      context: ctxEl?.getAttribute("data-proman-context") || "",
    };
  };
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const sessionIdRef = useRef(getSessionId());
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  const photoInputRef = useRef(null);

  // ---- Draggable bubble position ----
  // Stored as offsets from the bottom-right corner (px). Persists in localStorage so
  // techs can park it out of the way (e.g. bottom-left) and it stays put.
  const POS_KEY = "askproman_bubble_pos";
  const readPos = () => {
    try {
      const v = JSON.parse(localStorage.getItem(POS_KEY) || "null");
      if (v && Number.isFinite(v.right) && Number.isFinite(v.bottom)) return v;
    } catch { /* ignore */ }
    return { right: 20, bottom: 20 };
  };
  const [pos, setPos] = useState(readPos);
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origRight: 0, origBottom: 0, moved: false });

  const clampPos = (right, bottom) => {
    const maxRight = Math.max(0, window.innerWidth - 64);
    const maxBottom = Math.max(0, window.innerHeight - 64);
    return { right: Math.min(maxRight, Math.max(0, right)), bottom: Math.min(maxBottom, Math.max(0, bottom)) };
  };

  const onDragStart = (e) => {
    const t = e.touches ? e.touches[0] : e;
    dragRef.current = {
      dragging: true, moved: false,
      startX: t.clientX, startY: t.clientY,
      origRight: pos.right, origBottom: pos.bottom,
    };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
    window.addEventListener("touchmove", onDragMove, { passive: false });
    window.addEventListener("touchend", onDragEnd);
  };
  const onDragMove = (e) => {
    if (!dragRef.current.dragging) return;
    const t = e.touches ? e.touches[0] : e;
    const dx = t.clientX - dragRef.current.startX;
    const dy = t.clientY - dragRef.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > 6) dragRef.current.moved = true;
    if (e.touches) e.preventDefault();
    setPos(clampPos(dragRef.current.origRight - dx, dragRef.current.origBottom - dy));
  };
  const onDragEnd = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
    window.removeEventListener("touchmove", onDragMove);
    window.removeEventListener("touchend", onDragEnd);
    try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
  };
  useEffect(() => {
    // If viewport shrinks (rotate/resize), keep the bubble in-bounds.
    const onResize = () => setPos(p => clampPos(p.right, p.bottom));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
  }, [pos]);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  // Swipe-down-to-dismiss on mobile — track touch drag distance on the
  // grab handle; if the user pulls the panel down > 80px, we close it.
  // Live translateY visualises the drag so it feels native.
  const [dragY, setDragY] = useState(0);
  const dragStartYRef = useRef(null);

  // Bind Escape key to close (desktop UX affordance)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // A question handed off from Ctrl+K / search fires once the panel is open
  useEffect(() => {
    if (open && pendingAskRef.current && !busy) {
      const m = pendingAskRef.current; pendingAskRef.current = null;
      send(m);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load history on first open
  useEffect(() => {
    if (!open || msgs.length > 0) return;
    (async () => {
      try {
        const r = await api.get(`/agent/session/${sessionIdRef.current}`);
        setMsgs(r.data.messages || []);
      } catch { /* first-time — no history yet */ }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    const nowIso = new Date().toISOString();
    setMsgs(prev => [...prev, { role: "user", text, at: nowIso }]);
    setBusy(true);
    try {
      const r = await api.post("/agent/chat", { session_id: sessionIdRef.current, message: text, page: pageSnapshot() });
      const actions = r.data.actions || [];
      setMsgs(prev => [...prev, { role: "assistant", text: r.data.reply || "…", actions, at: new Date().toISOString() }]);
      const nav = actions.find(a => a.client_action === "navigate" && a.ok && a.path);
      if (nav && nav.path !== location.pathname + location.search) {
        setTimeout(() => { navigate(nav.path); toast.success(`Radar opened ${nav.label}`); }, 700);
      }
      // Handle any client-side hints (e.g. open camera)
      const photoAction = actions.find(a => a.client_action === "take_photo" && a.ok && a.job_id);
      if (photoAction) {
        setPendingPhoto({ job_id: photoAction.job_id, kind: photoAction.photo_kind || "after" });
        setTimeout(() => photoInputRef.current?.click(), 100);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Radar couldn't respond just now");
      setMsgs(prev => [...prev, { role: "assistant", text: "Sorry, something went wrong. Try again?", actions: [], at: new Date().toISOString() }]);
    } finally {
      setBusy(false);
    }
  };

  const clearSession = async () => {
    if (!window.confirm("Clear this chat? Your history with Radar will be forgotten.")) return;
    try { await api.delete(`/agent/session/${sessionIdRef.current}`); } catch {/* ok */}
    sessionIdRef.current = newSessionId();
    setMsgs([]);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) { toast.error("That recording was too short."); return; }
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        const t = toast.loading("Transcribing…");
        try {
          const r = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const txt = (r.data.text || "").trim();
          if (txt) { toast.success("Got it — sending", { id: t }); await send(txt); }
          else { toast.error("Couldn't hear anything — try again", { id: t }); }
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcription failed", { id: t });
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone blocked — check browser permissions");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (!file || !pendingPhoto) { setPendingPhoto(null); return; }
    const target = pendingPhoto;
    setPendingPhoto(null);
    const t = toast.loading("Uploading photo…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", target.kind);
      const up = await api.post(`/jobs/${target.job_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fileId = up.data?.file_id;
      toast.success("Photo attached", { id: t });
      // Fire-and-forget AI auto-tag (kitchen · faucet · leak). Fail silent.
      let aiSummary = "";
      if (fileId) {
        const t2 = toast.loading("Tagging photo with AI…");
        try {
          const tagRes = await api.post(`/jobs/${target.job_id}/photos/ai-tag-batch`, { file_ids: [fileId] });
          const first = (tagRes.data?.results || [])[0];
          if (first) {
            const tags = (first.ai_tags || []).join(" · ");
            const note = (first.ai_note || "").trim();
            aiSummary = [tags, note].filter(Boolean).join(" — ");
            toast.success(aiSummary ? `AI tagged: ${aiSummary}` : "Photo tagged", { id: t2 });
          } else {
            toast.dismiss(t2);
          }
        } catch (err) {
          toast.dismiss(t2);  // silent — worker still has the photo
        }
      }
      // Ping the agent so it acknowledges + you can chain another action
      const ackMsg = aiSummary
        ? `Photo uploaded and AI-tagged: ${aiSummary}.`
        : "Photo uploaded — attached to the job.";
      await send(ackMsg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed", { id: t });
    }
  };

  const { user } = useAuth();
  const isWorker = user?.role === "worker";
  // Only show Mario's status-email suggestion on Chew Flats pages — that's the
  // only portfolio he PMs (LaSalle Life Services).
  const onChewPage = typeof window !== "undefined" && /chew/i.test(window.location.pathname);
  const suggested = isWorker ? [
    "What's my next job?",
    "Am I clocked in?",
    "Mark item 1 done on my current job",
    "Add a note: tenant wasn't home, left card on door",
    "Clock me out",
  ] : [
    onChewPage ? "How do I reply to Mario's status email?" : "Draft a status email for the property manager",
    "Where do I pin jobs to tomorrow's trucks?",
    "Who's assigned tomorrow?",
    "Clock Alejandro into WO 3464 — he forgot to punch in",
    "Add a job for a leaking faucet at 123 Main St, unit 2",
  ];

  return (
    <>
      {/* Bubble (collapsed) — draggable, position persisted in localStorage */}
      {!open && !isTeamChat && (
        <button
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          onClick={(e) => { if (dragRef.current.moved) { e.preventDefault(); return; } setOpen(true); }}
          data-testid="ask-proman-bubble"
          aria-label="Ask Radar AI assistant — drag to move"
          style={{ right: pos.right, bottom: pos.bottom, touchAction: "none" }}
          className="fixed z-40 h-16 w-16 bg-gradient-to-br from-[#00C4CC] to-[#0891b2] text-white shadow-[6px_6px_0px_#0f172a] border-2 border-[#0f172a] flex items-center justify-center hover:scale-105 transition-transform focus:outline-none focus:ring-4 focus:ring-[#00C4CC]/40 cursor-grab active:cursor-grabbing"
        >
          <Sparkle weight="fill" size={30} />
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 border-2 border-white text-white text-[10px] font-black flex items-center justify-center animate-pulse">AI</span>
        </button>
      )}

      {/* Panel (expanded) — anchors to whatever spot the bubble was parked at.
          On phones we force it top-anchored with `inset` bounds so the header
          (with the X button) is ALWAYS in the viewport — otherwise a tall
          72vh panel + browser chrome + scroll can push the header off-screen
          leaving no way to close it. */}
      {open && !isTeamChat && (
        <div
          data-testid="ask-proman-panel"
          style={typeof window !== "undefined" && window.innerWidth < 640
                  ? (dragY > 0 ? { transform: `translateY(${dragY}px)`,
                                    transition: dragStartYRef.current === null ? "transform 200ms" : "none" }
                                : undefined)
                  : {
                      // Clamp panel inside viewport — the FAB can be dragged
                      // anywhere, but the 400×640 panel must always have its
                      // header (with the X button) visible.
                      right: Math.max(10, Math.min(pos.right, window.innerWidth - 420)),
                      bottom: Math.max(10, Math.min(pos.bottom, window.innerHeight - 660)),
                    }}
          className={
            "fixed z-[60] bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] flex flex-col " +
            "inset-x-3 top-3 bottom-3 max-h-[calc(100vh-1.5rem)] " +
            "sm:inset-auto sm:top-auto sm:bottom-auto sm:w-[400px] sm:h-[min(72vh,640px)]"
          }
        >
          {/* Mobile swipe-down grab handle — drag > 80px to dismiss.
              Only interactive on touch screens; on desktop it just shows
              as a subtle pill (harmless). */}
          <div
            data-testid="ask-proman-swipe-handle"
            className="sm:hidden pt-2 pb-1 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"
            onTouchStart={(e) => {
              dragStartYRef.current = e.touches[0].clientY;
            }}
            onTouchMove={(e) => {
              if (dragStartYRef.current == null) return;
              const dy = e.touches[0].clientY - dragStartYRef.current;
              // Only track downward pulls, ignore upward flicks
              setDragY(Math.max(0, dy));
            }}
            onTouchEnd={() => {
              if (dragY > 80) { setOpen(false); }
              dragStartYRef.current = null;
              setDragY(0);   // snap back if not enough
            }}
          >
            <div className="h-1 w-10 bg-slate-300 rounded-full"/>
          </div>
          {/* Hidden photo input — camera OR gallery on mobile (no capture attr = both options) */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            onChange={onPhotoPicked}
            data-testid="ask-proman-photo-input"
            className="hidden"
          />
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b-2 border-[#0f172a] bg-gradient-to-r from-[#00C4CC] to-[#0891b2] text-white">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkle weight="fill" size={20} />
              <div className="min-w-0">
                <div className="font-display font-black text-lg leading-none">Ask Radar</div>
                <div className="text-[10px] font-black uppercase tracking-widest opacity-90">AI · Claude Sonnet</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearSession} data-testid="ask-proman-clear-btn" aria-label="Clear chat"
                className="p-1.5 hover:bg-white/20 rounded-sm">
                <Trash weight="bold" size={16} />
              </button>
              <button onClick={() => setOpen(false)} data-testid="ask-proman-close-btn" aria-label="Close chat"
                className="p-1.5 hover:bg-white/20 rounded-sm">
                <X weight="bold" size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50 relative" data-testid="ask-proman-messages">
            {/* Floating close pill — belt-and-suspenders. Sits on top of the
                messages so users can ALWAYS close, even if the sticky header
                slips off-screen from browser chrome quirks. */}
            <button
              onClick={() => setOpen(false)}
              data-testid="ask-proman-close-floating"
              aria-label="Close chat"
              className="sticky top-0 float-right ml-2 mb-2 h-8 w-8 rounded-full bg-[#0f172a] text-white shadow-lg hover:bg-red-600 flex items-center justify-center z-10 border-2 border-white">
              <X weight="bold" size={16} />
            </button>
            {msgs.length === 0 && !busy && (
              <div className="space-y-3">
                <div className="rugged-card p-3 bg-white text-sm text-slate-700">
                  {isWorker
                    ? "Hi 👋 I'm Radar. Tap the mic and dictate your next WO ('kitchen leak at 306 Levrington unit 2 urgent'), or type things like \"clock me in\" or \"mark item 3 done\" — I'll handle it."
                    : "Hi 👋 I'm Radar — your guide to everything in here. Ask \"how do I…\" or \"where is…\" and I'll explain and take you there. I can also create/assign/schedule WOs or answer about jobs, techs and schedules."}
                </div>
                <div className="grid gap-1.5">
                  {suggested.map((s, i) => (
                    <button key={i} onClick={() => send(s)}
                      data-testid={`ask-proman-suggest-${i}`}
                      className="text-left px-3 py-2 bg-white border-2 border-[#0f172a] text-xs font-bold text-slate-700 hover:bg-[#00C4CC] hover:text-white transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {msgs.map((m, i) => (
              <MsgBubble key={i} m={m} />
            ))}

            {busy && (
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500" data-testid="ask-proman-thinking">
                <span className="inline-block w-2 h-2 bg-[#00C4CC] animate-pulse rounded-full"/>
                <span className="inline-block w-2 h-2 bg-[#00C4CC] animate-pulse rounded-full" style={{animationDelay:"120ms"}}/>
                <span className="inline-block w-2 h-2 bg-[#00C4CC] animate-pulse rounded-full" style={{animationDelay:"240ms"}}/>
                <span>Radar is thinking…</span>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="p-3 border-t-2 border-[#0f172a] bg-white">
            <div className="flex items-end gap-1.5">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder={recording ? "Listening…" : "Ask a question or 'Add a job at…'"}
                disabled={recording || busy}
                rows={1}
                data-testid="ask-proman-input"
                className="flex-1 min-w-0 h-11 max-h-24 px-3 py-2 border-2 border-[#0f172a] text-sm font-medium resize-none focus:outline-none focus:ring-2 focus:ring-[#00C4CC]/40"
              />
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={busy}
                data-testid="ask-proman-mic-btn"
                aria-label={recording ? "Stop recording" : "Start voice input"}
                className={`h-11 w-11 flex items-center justify-center border-2 border-[#0f172a] font-black transition-colors ${
                  recording ? "bg-red-500 text-white animate-pulse" : "bg-white text-[#0f172a] hover:bg-[#0f172a] hover:text-white"
                }`}
              >
                <Microphone weight="fill" size={18} />
              </button>
              <button
                onClick={() => send()}
                disabled={busy || recording || !input.trim()}
                data-testid="ask-proman-send-btn"
                aria-label="Send message"
                className="tactile-btn h-11 w-11 flex items-center justify-center bg-[#00C4CC] text-white font-black disabled:opacity-40"
              >
                <PaperPlaneTilt weight="fill" size={18} />
              </button>
            </div>
            <p className="mt-1.5 text-[10px] text-slate-400 font-medium">
              Enter to send · Shift+Enter for new line · Radar can make mistakes — always double-check job details
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function MsgBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={isUser ? "ask-proman-user-msg" : "ask-proman-assistant-msg"}
        className={`max-w-[85%] px-3 py-2 text-sm font-medium whitespace-pre-wrap break-words border-2 ${
          isUser
            ? "bg-[#0f172a] text-white border-[#0f172a] rounded-sm"
            : "bg-white text-slate-800 border-slate-200 rounded-sm shadow-sm"
        }`}
      >
        {isUser ? m.text : (m.text || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
          part.startsWith("**") && part.endsWith("**") ? <strong key={i} className="font-black">{part.slice(2, -2)}</strong> : part)}
        {!isUser && Array.isArray(m.actions) && m.actions.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5">
            {m.actions.map((a, i) => (
              <div key={i} className={`flex items-center gap-1.5 text-xs font-black uppercase tracking-widest ${a.ok ? "text-emerald-600" : "text-red-600"}`}>
                <CheckCircle weight="fill" size={14} />
                {a.client_action === "navigate" && a.path ? (
                  <Link to={a.path} className="inline-flex items-center gap-1 px-2 py-1 bg-[#00C4CC] text-white hover:bg-[#0891b2] normal-case tracking-normal text-xs" data-testid={`ask-proman-navigate-${i}`}>
                    <ArrowSquareOut weight="bold" size={12}/> Take me there → {a.label}
                  </Link>
                ) : a.job_id ? (
                  <Link to={`/admin/jobs/${a.job_id}`} className="underline hover:text-[#00C4CC]" data-testid={`ask-proman-action-link-${i}`}>
                    {a.message || `${a.type} done`}
                  </Link>
                ) : (
                  <span>{a.message || `${a.type} — ${a.ok ? "done" : "failed"}`}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
